/**
 * Translation helpers for the front-end bundles.
 *
 * Thin wrappers over wp.i18n (the `wp-i18n` script WordPress ships, listed
 * as a dependency of every bundle that uses this), falling back to the
 * English text when it is missing. Every string goes through the
 * "big-squash" text domain; the translations for a bundle arrive through
 * wp_set_script_translations() from languages/big-squash-<locale>-<hash>.json.
 *
 * Write strings as literals so bin/i18n.js can collect them:
 *   __('Sign up')                          plain text
 *   _x('In', 'entry status')               text with a disambiguating context
 *   _n('%d place', '%d places', count)     singular / plural
 *   sprintf(__('%1$d of %2$d places'), a, b)   values go through sprintf, never template literals
 * Run `npm run i18n` after adding or changing any.
 */

const DOMAIN = 'big-squash';

const lib = () => (window.wp && window.wp.i18n) || null;

export function __(text) {
    const i18n = lib();
    return i18n ? i18n.__(text, DOMAIN) : text;
}

export function _x(text, context) {
    const i18n = lib();
    return i18n ? i18n._x(text, context, DOMAIN) : text;
}

export function _n(single, plural, count) {
    const i18n = lib();
    return i18n ? i18n._n(single, plural, count, DOMAIN) : (Number(count) === 1 ? single : plural);
}

/** printf-style formatting with %s, %d and positional %1$s placeholders. */
export function sprintf(format, ...args) {
    const i18n = lib();
    if (i18n && typeof i18n.sprintf === 'function') {
        return i18n.sprintf(format, ...args);
    }
    let auto = 0;
    return String(format).replace(/%(?:(\d+)\$)?([sdf%])/g, (m, pos, type) => {
        if (type === '%') {
            return '%';
        }
        const value = pos ? args[Number(pos) - 1] : args[auto++];
        if (type === 'd') {
            return String(parseInt(value, 10) || 0);
        }
        if (type === 'f') {
            return String(parseFloat(value) || 0);
        }
        return value == null ? '' : String(value);
    });
}

/** The page language as a BCP 47 tag ("es-ES"), for toLocaleDateString and friends. */
export function htmlLang() {
    return (document.documentElement && document.documentElement.lang) || 'en';
}

/** 1 → "1st" in English, "1.º" in Spanish. */
export function ordinal(n) {
    const value = Number(n) || 0;
    const mod100 = value % 100;
    const mod10 = value % 10;
    let format;
    if (mod100 >= 11 && mod100 <= 13) {
        format = _x('%dth', 'ordinal, other');
    } else if (mod10 === 1) {
        format = _x('%dst', 'ordinal, 1');
    } else if (mod10 === 2) {
        format = _x('%dnd', 'ordinal, 2');
    } else if (mod10 === 3) {
        format = _x('%drd', 'ordinal, 3');
    } else {
        format = _x('%dth', 'ordinal, other');
    }
    return sprintf(format, value);
}
